import React, { useRef, useState } from 'react'
import { Box, Button, Tooltip, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import RestoreIcon from '@mui/icons-material/Restore'
import { globalGetService, globalPutService } from '../../../utils/globalApiServices'
import { checkApiStatus, getGlobalCookie } from '../../../utils'
import { enqueueSnackbar } from 'notistack'
import { getApprovalWorkingGroupsDropdownApi, approvalActionApi } from '../apiService'
import { trackActivity } from '../../../utils/mixpanel'
import { useDispatch, useSelector } from 'react-redux'
import { getStorage } from '../../../shared_elements/actionCreators'
import fileUpload from '../../../utils/fileUpload'
import ApprovalActionDialog from './ApprovalActionDialog'
import RevertStageDialog from './RevertStageDialog'
import MultiApprovalTimeline from './MultiApprovalTimeline'

const MEMBER_STATUS = { PENDING: 1, APPROVED: 2, REJECTED: 3 }
const STAGE_STATUS = { NOT_STARTED: 'not_started', IN_PROGRESS: 'in_progress', APPROVED: 'approved', REJECTED: 'rejected' }

export const getOverallStatus = (controlStages = []) => {
    if (!controlStages.length) return STAGE_STATUS.NOT_STARTED
    if (controlStages.some(s => s.status === STAGE_STATUS.REJECTED)) return STAGE_STATUS.REJECTED
    if (controlStages.every(s => s.status === STAGE_STATUS.APPROVED)) return STAGE_STATUS.APPROVED
    return STAGE_STATUS.IN_PROGRESS
}

export const getActiveStage = (controlStages = []) =>
    controlStages.find(s => s.status !== STAGE_STATUS.APPROVED) || controlStages[controlStages.length - 1]

export const MultiApprovalTracker = ({ controlStages = [], disabled = false }) => {
    if (!controlStages.length) return null
    const activeStage = getActiveStage(controlStages)

    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, opacity: disabled ? 0.55 : 1, filter: disabled ? 'grayscale(1)' : 'none' }}>
            {controlStages.map((stage, idx) => {
                const isApproved = stage.status === STAGE_STATUS.APPROVED
                const isRejected = stage.status === STAGE_STATUS.REJECTED
                const isActive = activeStage?.control_stage === stage.control_stage
                const nodeColor = isApproved ? '#1E9E5A' : isRejected ? '#D64545' : (isActive && !disabled) ? 'primary.main' : '#c9c9d6'
                const lineColor = isApproved ? '#1E9E5A' : isRejected ? '#D64545' : '#E7E9F2'
                return (
                    <Box key={stage.control_stage} sx={{ flex: 1, textAlign: 'center', position: 'relative' }}>
                        {idx !== controlStages.length - 1 && (
                            <Box sx={{ position: 'absolute', top: '13px', left: '50%', width: '100%', height: '2px', bgcolor: lineColor, zIndex: 0 }} />
                        )}
                        <Box sx={{
                            width: 26, height: 26, borderRadius: '50%', bgcolor: nodeColor, color: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
                            mx: 'auto', mb: 1, position: 'relative', zIndex: 1
                        }}>
                            {isApproved ? <CheckIcon sx={{ fontSize: 16 }} /> : isRejected ? <CloseIcon sx={{ fontSize: 16 }} /> : idx + 1}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            <Typography variant='caption' sx={{ fontFamily: 'Roboto-Bold', fontWeight: 800 }}>{stage.stage_name}</Typography>
                        </Box>
                        <Typography
                            variant='caption'
                            sx={{ fontWeight: 700, color: isApproved ? '#1E9E5A' : isRejected ? '#D64545' : (isActive && !disabled) ? 'primary.main' : 'text.secondary' }}
                        >
                            {isApproved ? 'Approved' : isRejected ? 'Rejected' : isActive ? 'Pending' : 'Not Started'}
                        </Typography>
                    </Box>
                )
            })}
        </Box>
    )
}

const MultiApprovalFlow = ({ invoiceSlug, leaseSlug, controlStages = [], getApprovalDetails, invoiceStatus, getInvoice }) => {
    const dispatch = useDispatch()
    const { storageDetails } = useSelector(state => state.leases)
    const currentUserId = JSON.parse(getGlobalCookie('lessorAccess'))?.user?.id
    // Approve / Reject dialog - also carries the working group selection when the active stage hasn't been assigned one yet.
    const [approveRejectDialog, setApproveRejectDialog] = useState({ open: false, remark: '', remarkError: '', attachments: [], workingGroups: [], groupsLoading: false, selectedGroup: null, groupError: '', groupPickerVisible: false })
    const [decisionSubmitting, setDecisionSubmitting] = useState(null) // 'approve' | 'reject' - shared by the approve/reject dialog and the revert dialog below
    // Revert-to-stage dialog - only shown when rejecting at the Final Control (last) stage, so the rejecter can choose which earlier stage the chain reverts to. Also carries the working group selection when the chosen stage has none assigned.
    const [revertDialog, setRevertDialog] = useState({ open: false, stage: null, error: '', workingGroups: [], groupsLoading: false, selectedGroup: null, groupError: '' })
    // Approval status timeline dialog
    const [timelineDialogOpen, setTimelineDialogOpen] = useState(false)

    const overallStatus = getOverallStatus(controlStages)
    const activeStage = getActiveStage(controlStages)
    const currentUserMember = activeStage?.members?.find(m => m.member_info?.user_id === currentUserId)
    const isFinalStage = controlStages.length > 0 && activeStage?.control_stage === controlStages[controlStages.length - 1].control_stage
    // The first stage has nowhere earlier to revert to, so rejecting isn't a valid action there.
    const isFirstStage = controlStages.length > 0 && activeStage?.control_stage === controlStages[0].control_stage
    // Treat a stage still awaiting its first trigger, or one whose decision was rejected (and the chain reverted), the same way: both need a working group picked before a decision can be recorded.
    const isActiveNotStarted = activeStage?.status === STAGE_STATUS.NOT_STARTED || activeStage?.status === STAGE_STATUS.REJECTED
    const isActiveInProgress = activeStage?.status === STAGE_STATUS.IN_PROGRESS
    const activeStageIndex = controlStages.findIndex(s => s.control_stage === activeStage?.control_stage)
    const nextStage = activeStageIndex > -1 ? controlStages[activeStageIndex + 1] : null
    // Approving the active stage advances the flow to nextStage - if that stage has no auto-detected working group, one must be picked now so it isn't left with no assignable members.
    const needsNextStageWorkingGroup = !!nextStage && !nextStage.auto_detect_working_group
    // The stage chosen to revert to (on a final-stage reject) may itself have no working group assigned.
    const revertStageNeedsWorkingGroup = !!revertDialog.stage && !revertDialog.stage.auto_detect_working_group
    const handleItemClicked = (page_type) => trackActivity('Item Clicked', { page_title: 'Multi Approval Flow', page_type })

    // Guards against fetchWorkingGroups firing twice for a single dialog open (e.g. a double click before the dialog blocks further clicks) - a ref is needed since state updates aren't committed synchronously between the two calls.
    const groupsFetchingRef = useRef(false)
    const fetchWorkingGroups = () => {
        if (groupsFetchingRef.current) return
        groupsFetchingRef.current = true
        setApproveRejectDialog(prev => ({ ...prev, groupsLoading: true }))
        globalGetService(getApprovalWorkingGroupsDropdownApi(leaseSlug)).then(response => {
            groupsFetchingRef.current = false
            setApproveRejectDialog(prev => ({
                ...prev,
                groupsLoading: false,
                workingGroups: checkApiStatus(response) ? (response.data.data || []) : prev.workingGroups
            }))
        }).catch(() => {
            groupsFetchingRef.current = false
            setApproveRejectDialog(prev => ({ ...prev, groupsLoading: false }))
        })
    }

    // Same guard/fetch pattern as fetchWorkingGroups above, but for the revert dialog's own working group dropdown.
    const revertGroupsFetchingRef = useRef(false)
    const fetchRevertWorkingGroups = () => {
        if (revertGroupsFetchingRef.current) return
        revertGroupsFetchingRef.current = true
        setRevertDialog(prev => ({ ...prev, groupsLoading: true }))
        globalGetService(getApprovalWorkingGroupsDropdownApi(leaseSlug)).then(response => {
            revertGroupsFetchingRef.current = false
            setRevertDialog(prev => ({
                ...prev,
                groupsLoading: false,
                workingGroups: checkApiStatus(response) ? (response.data.data || []) : prev.workingGroups
            }))
        }).catch(() => {
            revertGroupsFetchingRef.current = false
            setRevertDialog(prev => ({ ...prev, groupsLoading: false }))
        })
    }

    // ── Approve / Reject ─────────────────────────────────────────────────────
    const openApproveRejectDialog = () => {
        dispatch(getStorage({ lease_slug: leaseSlug, module_name: 'invoice_approval' }))
        setApproveRejectDialog(prev => ({ ...prev, open: true, selectedGroup: null, groupError: '', groupPickerVisible: false }))
    }
    const closeApproveRejectDialog = () => {
        setApproveRejectDialog({ open: false, remark: '', remarkError: '', attachments: [], workingGroups: [], groupsLoading: false, selectedGroup: null, groupError: '', groupPickerVisible: false })
    }
    const handleFileChange = (files) => setApproveRejectDialog(prev => ({ ...prev, attachments: files }))
    const handleUpload = async (slug, mode) => {
        await fileUpload({
            attachments: approveRejectDialog.attachments,
            module_name: 'invoice_approval_status_history',
            slug,
            query: { record_action_type: mode },
            onStart: () => setDecisionSubmitting(true),
            onComplete: () => setDecisionSubmitting(false),
            onSuccess: (success_msg) => {
                enqueueSnackbar(success_msg, { variant: 'success' })
                getApprovalDetails()
            },
            onError: (err) => {
                enqueueSnackbar(err?.message || 'Error while uploading files', { variant: 'error' })
            }
        })
    }

    const closeRevertDialog = () => {
        setRevertDialog({ open: false, stage: null, error: '', workingGroups: [], groupsLoading: false, selectedGroup: null, groupError: '' })
    }

    // Picking which stage to revert to may land on a stage with no working group of its own -
    // in that case its working group options need loading before the rejecter can pick one.
    const handleRevertStageChange = (stage) => {
        setRevertDialog(prev => ({ ...prev, stage, error: '', selectedGroup: null, groupError: '' }))
        if (stage && !stage.auto_detect_working_group) fetchRevertWorkingGroups()
    }

    // Shared submission for both a normal approve/reject and a final-stage reject that was deferred until a revert-to-stage was chosen.
    const submitDecision = async (actionStatus, extraPayload = {}) => {
        const approvalSlug = currentUserMember?.slug
        if (!approvalSlug) {
            enqueueSnackbar('Unable to record your decision - you are not listed as a member of this stage\'s working group.', {
                variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'right' }
            })
            return
        }
        const submittingKey = actionStatus === MEMBER_STATUS.APPROVED ? 'approve' : 'reject'
        setDecisionSubmitting(submittingKey)
        const payload = { status: actionStatus, remark: approveRejectDialog.remark.trim(), ...extraPayload }
        const actionResponse = await globalPutService(approvalActionApi(invoiceSlug, approvalSlug), payload)
        setDecisionSubmitting(null)
        if (!checkApiStatus(actionResponse)) {
            enqueueSnackbar(actionResponse.data?.message || 'Action failed', {
                variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'right' }
            })
            return
        }
        enqueueSnackbar(actionResponse.data?.message || (actionStatus === MEMBER_STATUS.APPROVED ? 'Approved' : 'Rejected'), {
            variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'right' }
        })

        const attachments = approveRejectDialog.attachments
        closeApproveRejectDialog()
        closeRevertDialog()
        getApprovalDetails()

        if (attachments.length) {
            // The action response already carries the status-history slug attachments upload against - no need for a separate timeline lookup.
            const statusHistorySlug = actionResponse.data?.data?.status_history_slug
            if (statusHistorySlug) handleUpload(statusHistorySlug, 'created')
        }

        getInvoice()
    }

    const handleApproveOrReject = (actionStatus) => {
        // Clear any stale error from a previous attempt (e.g. the "reason required" error left over from a prior Reject click) so it doesn't linger once a different action is tried.
        setApproveRejectDialog(prev => ({ ...prev, remarkError: '' }))
        // Rejecting doesn't advance to nextStage, so the Next Approver Group picker (only relevant to Approve) shouldn't stay visible if it was revealed by an earlier Approve click.
        if (actionStatus === MEMBER_STATUS.REJECTED && approveRejectDialog.groupPickerVisible) {
            setApproveRejectDialog(prev => ({ ...prev, groupPickerVisible: false, selectedGroup: null, groupError: '' }))
        }
        // Approving advances the flow into nextStage - if it has no auto-detected working group, one must be picked before the decision can be recorded. Rejecting doesn't advance, so it's exempt.
        if (actionStatus === MEMBER_STATUS.APPROVED && needsNextStageWorkingGroup) {
            // First Approve click just reveals the picker and loads the options - the actual decision is only recorded once a group has been chosen on a subsequent click.
            if (!approveRejectDialog.groupPickerVisible) {
                setApproveRejectDialog(prev => ({ ...prev, groupPickerVisible: true }))
                fetchWorkingGroups()
                return
            }
            if (!approveRejectDialog.selectedGroup) {
                setApproveRejectDialog(prev => ({ ...prev, groupError: 'Please select Next Approver Group' }))
                return
            }
            if (!approveRejectDialog.selectedGroup.members?.length) {
                enqueueSnackbar(`Please add a member in "${approveRejectDialog.selectedGroup.name}" working group`, {
                    variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'right' }
                })
                return
            }
        }
        if (actionStatus === MEMBER_STATUS.REJECTED && !approveRejectDialog.remark.trim()) {
            setApproveRejectDialog(prev => ({ ...prev, remarkError: 'Please enter a reason for rejection' }))
            return
        }
        // Rejecting at the last stage (Final Control) needs the rejecter to pick which earlier
        // stage to revert to, instead of always reverting to stage 1.
        if (actionStatus === MEMBER_STATUS.REJECTED && isFinalStage) {
            setApproveRejectDialog(prev => ({ ...prev, open: false }))
            setRevertDialog({ open: true, stage: null, error: '', workingGroups: [], groupsLoading: false, selectedGroup: null, groupError: '' })
            return
        }
        submitDecision(actionStatus, {
            ...(actionStatus === MEMBER_STATUS.REJECTED ? { revert_to_stage: 1 } : {}),
            ...(actionStatus === MEMBER_STATUS.APPROVED && needsNextStageWorkingGroup ? { working_group_id: approveRejectDialog.selectedGroup.id } : {})
        })
    }

    const handleConfirmRevert = () => {
        if (!revertDialog.stage) {
            setRevertDialog(prev => ({ ...prev, error: 'Please select a working group to revert to' }))
            return
        }
        // The stage being reverted to may have no working group of its own - one must be picked before the revert can be recorded.
        if (revertStageNeedsWorkingGroup) {
            if (!revertDialog.selectedGroup) {
                setRevertDialog(prev => ({ ...prev, groupError: 'Please select Previous Approver Group' }))
                return
            }
            if (!revertDialog.selectedGroup.members?.length) {
                enqueueSnackbar(`Please add a member in "${revertDialog.selectedGroup.name}" working group`, {
                    variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'right' }
                })
                return
            }
        }
        submitDecision(MEMBER_STATUS.REJECTED, {
            revert_to_stage: revertDialog.stage.control_stage,
            ...(revertStageNeedsWorkingGroup ? { working_group_id: revertDialog.selectedGroup.id } : {})
        })
    }
    if (!controlStages.length) return null
    const isNonDraft = invoiceStatus?.value !== 1
    // Base label + stage name are rendered as two stacked lines inside the button (see JSX below)
    const buttonBaseLabel = (() => {
        if (overallStatus === STAGE_STATUS.APPROVED) return 'Approved'
        if (overallStatus === STAGE_STATUS.REJECTED) return 'REJECTED — APPROVE / REJECT'
        if (isActiveNotStarted || isActiveInProgress) return currentUserMember ? (isFirstStage ? 'Approve' : 'Approve / Reject') : 'Pending'
        return 'Approval'
    })()
    const buttonStageName = (isActiveNotStarted || isActiveInProgress) ? activeStage?.stage_name : null
    const isDisabled = isNonDraft || overallStatus === STAGE_STATUS.APPROVED || ((isActiveNotStarted || isActiveInProgress) && !currentUserMember)
    const handleButtonClick = () => {
        if ((isActiveInProgress || isActiveNotStarted) && currentUserMember) openApproveRejectDialog()
    }

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                    variant='outlined'
                    size='small'
                    disabled={isDisabled}
                    onClick={handleButtonClick}
                    fullWidth
                    sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: (buttonBaseLabel.length + (buttonStageName?.length || 0)) > 24 ? '0.78rem' : undefined,
                        ...(overallStatus === STAGE_STATUS.APPROVED ? {
                            color: '#2e714d', borderColor: '#0e8c4d', backgroundColor: '#aae5aa',
                            '&.Mui-disabled': { color: '#2e714d', borderColor: '#0e8c4d', backgroundColor: '#aae5aa', cursor: 'not-allowed', pointerEvents: 'auto' }
                        } : overallStatus === STAGE_STATUS.REJECTED ? {
                            color: '#D64545', borderColor: '#D64545', backgroundColor: '#FBDADA'
                        } : (isActiveInProgress || isActiveNotStarted) && !currentUserMember && invoiceStatus?.value === 1 ? {
                            color: '#b45309', borderColor: '#fcd34d', backgroundColor: '#fffbeb',
                            '&.Mui-disabled': { color: '#b45309', borderColor: '#fcd34d', backgroundColor: '#fffbeb', cursor: 'not-allowed', pointerEvents: 'auto' }
                        } : {}),
                        ...((isActiveInProgress || isActiveNotStarted) && !currentUserMember && invoiceStatus?.value !== 1 ? {
                            '&.Mui-disabled': { cursor: 'not-allowed', pointerEvents: 'auto' }
                        } : {})
                    }}
                >
                    {buttonBaseLabel}{buttonStageName ? ` (${buttonStageName})` : ''}
                </Button>
                <Tooltip title='Approval Flow Timeline' arrow>
                    <RestoreIcon
                        className='revision-history'
                        color='primary'
                        fontSize='small'
                        sx={{ cursor: 'pointer' }}
                        onClick={() => { setTimelineDialogOpen(true); handleItemClicked('RevisionHistoryIcon') }}
                    />
                </Tooltip>
            </Box>

            {(isActiveInProgress || isActiveNotStarted) && !currentUserMember && (
                <Typography variant='caption' sx={{ color: invoiceStatus?.value === 1 ? '#c1651fbf' : 'GrayText', display: 'block', mt: 0.5, px: 1, py: 0.5, borderRadius: 1 }}>
                    Only members of the assigned working group can change the approval status.
                </Typography>
            )}

            <ApprovalActionDialog
                open={approveRejectDialog.open}
                stageName={activeStage?.stage_name}
                remark={approveRejectDialog.remark}
                remarkError={approveRejectDialog.remarkError}
                actionLoading={decisionSubmitting}
                remainingStorage={storageDetails?.remaining_storage}
                disableReject={isFirstStage}
                needsWorkingGroup={needsNextStageWorkingGroup && approveRejectDialog.groupPickerVisible}
                leaseSlug={leaseSlug}
                workingGroups={approveRejectDialog.workingGroups}
                groupsLoading={approveRejectDialog.groupsLoading}
                selectedGroup={approveRejectDialog.selectedGroup}
                groupError={approveRejectDialog.groupError}
                onChangeGroup={(val) => setApproveRejectDialog(prev => ({ ...prev, selectedGroup: val, groupError: '' }))}
                onFocusGroup={() => setApproveRejectDialog(prev => ({ ...prev, groupError: '' }))}
                onChangeRemark={(val) => setApproveRejectDialog(prev => ({ ...prev, remark: val, remarkError: '' }))}
                onFocusRemark={() => setApproveRejectDialog(prev => ({ ...prev, remarkError: '' }))}
                onFileChange={handleFileChange}
                onApprove={() => handleApproveOrReject(MEMBER_STATUS.APPROVED)}
                onReject={() => handleApproveOrReject(MEMBER_STATUS.REJECTED)}
                onCancel={closeApproveRejectDialog}
            />

            <RevertStageDialog
                open={revertDialog.open}
                options={controlStages.filter(s => s.control_stage !== activeStage?.control_stage)}
                value={revertDialog.stage}
                error={revertDialog.error}
                loading={decisionSubmitting === 'reject'}
                needsWorkingGroup={revertStageNeedsWorkingGroup}
                leaseSlug={leaseSlug}
                workingGroups={revertDialog.workingGroups}
                groupsLoading={revertDialog.groupsLoading}
                selectedGroup={revertDialog.selectedGroup}
                groupError={revertDialog.groupError}
                onChange={handleRevertStageChange}
                onChangeGroup={(val) => setRevertDialog(prev => ({ ...prev, selectedGroup: val, groupError: '' }))}
                onFocusGroup={() => setRevertDialog(prev => ({ ...prev, groupError: '' }))}
                onCancel={closeRevertDialog}
                onConfirm={handleConfirmRevert}
            />

            {/* Revision history */}
            {timelineDialogOpen && (
                <MultiApprovalTimeline
                    invoiceSlug={invoiceSlug}
                    approvalSlugs={controlStages.flatMap(stage => (stage.members || []).map(m => m.slug))}
                    toggleModalFn={() => setTimelineDialogOpen(false)}
                />
            )}
        </>
    )
}

export default MultiApprovalFlow
